<!-- eslint-disable max-len -->
<template>
  <v-row justify="end" class="mr-12">
    <v-dialog
      v-model="dialog"
      persistent
      width="1024"
      @click:outside="dialog = false"
    >
      <template #activator="{ props }">
        <v-btn v-bind="props" class="text-capitalize text-white button-config">
          Cadastrar Caso de Uso
          <v-icon right class="ml-1">mdi-plus</v-icon>
        </v-btn>
      </template>

      <v-card>
        <v-card-title>
          <div class="d-flex justify-space-between">
            <span class="text-h5">Cadastrar Caso de Uso</span>
            <v-btn
              variant="plain"
              rounded="xl"
              prepend-icon="mdi-window-close"
              @click="dialog = false"
            ></v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-form ref="form" validate-on="lazy submit">
              <v-row>
                <v-col cols="9">
                  <v-text-field
                    v-model="nome"
                    required
                    label="Nome*"
                    placeholder="Nome do caso de uso"
                    :rules="nomeRules"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="gatilho"
                    label="Gatilho*"
                    placeholder="Ação que inicia a execução do caso de uso"
                    required
                    :rules="gatilhoRules"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="preCondicao"
                    label="Pré-Condição*"
                    placeholder="Condições que devem ser satisfeitas no início do caso de uso"
                    required
                    :rules="preCondicaoRules"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="descricao"
                    required
                    label="Descrição*"
                    placeholder="Descrição do caso de uso"
                    rows="3"
                    :rules="descricaoRules"
                  ></v-textarea>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="observacao"
                    required
                    label="Observação*"
                    placeholder="Informações adicionais sobre o caso de uso"
                    rows="3"
                    :rules="observacaoRules"
                  ></v-textarea>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-select
                    v-model="prioridade"
                    :items="['Alta', 'Média', 'Baixa']"
                    label="Prioridade*"
                    required
                    :rules="prioridadeRules"
                  ></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="6">
                  <v-select
                    v-model="storypoint"
                    :items="['0', '0.5', '1', '2', '3', '5', '8', '13']"
                    label="Storypoint"
                  ></v-select>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
          <small>*Campo obrigatório</small>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="blue-darken-1" variant="text" @click="submitForm">
            Salvar
            <v-progress-circular
              v-if="this.isLoading"
              class="ml-2"
              indeterminate
              size="20"
            />
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>
<script>
/**
 * @module UseCaseModal
 * @desc Modal de criação de CSU.
 * @vue-data {boolean} [dialog=false] - Flag que indica se o
 * diálogo está aberto ou fechado.
 * @vue-data {string} [nome=''] - O nome do caso de uso.
 * @vue-data {string} [prioridade=''] - A prioridade do caso de uso.
 * @vue-data {string} [identificador=''] - O identificador do caso de uso.
 * @vue-data {string} [descricao=''] - A descrição do caso de uso.
 * @vue-data {string} [storypoint=''] - O número de pontos de história do
 * caso de uso.
 * @vue-data {string} [gatilho=''] - O gatilho do caso de uso.
 * @vue-data {string} [preCondicao=''] - A pré-condição do caso de uso.
 * @vue-data {string} [observacao=''] - Observação adicional sobre o
 * caso de uso.
 * @vue-data {number} [idProjeto=null] - O ID do projeto associado ao caso
 * de uso, ou null se nenhum projeto estiver associado.
 * @vue-data {boolean} [isLoading=false] - Flag que indica se o caso de uso
 * ainda está sendo criado.
 */
import axios from 'axios';

import store from '@/store/store';

export default {
  props: {
    fetchUseCases: {
      type: Function,
    },
  },
  data: () => ({
    dialog: false,
    nome: '',
    prioridade: '',
    descricao: '',
    storypoint: '',
    gatilho: '',
    preCondicao: '',
    observacao: '',
    idProjeto: null,
    isLoading: false,
    nomeRules: [
      (v) => !!v || 'Nome é obrigatório',
      (v) => (v && v.length <= 100) || 'Nome deve ter no máximo 100 caracteres',
    ],
    gatilhoRules: [
      (v) => !!v || 'Gatilho é obrigatório',
      (v) =>
        (v && v.length <= 100) || 'Gatilho deve ter no máximo 100 caracteres',
    ],
    preCondicaoRules: [
      (v) => !!v || 'Pré-condição é obrigatória',
      (v) =>
        (v && v.length <= 200) ||
        'Pré-condição deve ter no máximo 200 caracteres',
    ],
    descricaoRules: [
      (v) => !!v || 'Descrição é obrigatória',
      (v) =>
        (v && v.length <= 500) || 'Descrição deve ter no máximo 500 caracteres',
    ],
    observacaoRules: [
      (v) => !!v || 'Observação é obrigatória',
      (v) =>
        (v && v.length <= 500) ||
        'Observação deve ter no máximo 500 caracteres',
    ],
    prioridadeRules: [(v) => !!v || 'Prioridade é obrigatória'],
  }),
  created() {
    this.idProjeto = this.$route.params.idProjeto;
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    /**
     * Lida com o evento de pressionar tecla. Se a tecla pressionada for ESC,
     * o modal é fechado.
     *
     * @param {Object} event Objeto do evento
     */
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.dialog = false;
      }
    },

    /**
     * Envia o formulário de criação.
     */
    async submitForm() {
      const isValid = (await this.$refs.form.validate()).valid;

      if (isValid) {
        const data = {
          // eslint-disable-next-line camelcase
          identificador: '',
          gatilho: this.gatilho,
          nome: this.nome,
          // eslint-disable-next-line camelcase
          pre_condicao: this.preCondicao,
          descricao: this.descricao,
          prioridade:
            this.prioridade == 'Média'
              ? 'MEDIA'
              : this.prioridade.toUpperCase(),
          ativo: true,
          storypoint: parseFloat(this.storypoint),
          observacao: this.observacao,
          // eslint-disable-next-line camelcase
          id_projeto: parseInt(this.$route.params.idProjeto),
        };

        try {
          this.isLoading = true;
          const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';
          const response = await axios.post(
            `${apiUrl}/api/v1/productBacklog`,
            data,
            {
              headers: {
                Authorization: `Bearer ${store.state.token}`,
              },
            },
          );
          this.$toast.success(response.data.msg);
          this.fetchUseCases();
        } catch (error) {
          console.log(error);
          this.$toast.error(error.response.data.msg);
        } finally {
          this.isLoading = false;
          this.dialog = false;
          this.limpaFormulario();
        }
      }
    },

    /**
     * Método auxiliar para limpar os campos do formulário após o seu envio.
     */
    limpaFormulario() {
      this.nome = '';
      this.prioridade = '';
      this.identificador = '';
      this.descricao = '';
      this.storypoint = '';
      this.gatilho = '';
      this.preCondicao = '';
      this.observacao = '';
    },
  },
};
</script>
<style scoped>
.button-config {
  background-color: #004984;
}
</style>
